/**
 * InstallerInvoiceDialog — "View Invoice" popup opened from JobOfferReviewWorkspace's
 * Offer Status card once an offer has been accepted and the installer has
 * submitted an invoice.
 *
 * Shows the Koredor-computed breakdown (base payout + signed change orders'
 * installer impact + field materials reimbursement — see
 * installer-invoice-amount-compute in supabase/functions), a link to the
 * underlying file, and — only for installer_upload invoices — an on-demand
 * "Parse with AI" action that reuses the existing receipt-parse function.
 * Parsing is purely informational: displayed for staff to eyeball against
 * the computed total, never used to adjust or block anything. The computed
 * total on this record remains the system of record regardless of what an
 * uploaded document says.
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Loader2, Sparkles } from "lucide-react";
import { supabase } from "@/lib/supabase";
import {
  InstallerPayoutsService,
  type InstallerPayoutRow,
  type InstallerPayoutInvoiceRow,
} from "@/services/installerPayouts.service";

interface ParsedInvoice {
  vendorName: string;
  invoiceNumber: string;
  purchasedAt: string;
  total: number;
  currency: string;
  warnings: string[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payout: InstallerPayoutRow;
  invoice: InstallerPayoutInvoiceRow;
  onVoided: () => void;
}

function money(n: number): string {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

export function InstallerInvoiceDialog({ open, onOpenChange, payout, invoice, onVoided }: Props) {
  const [openingFile, setOpeningFile] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParsedInvoice | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleViewFile = async () => {
    setOpeningFile(true);
    const { url, error } = await InstallerPayoutsService.getInvoiceDownloadUrl(invoice.file_path);
    setOpeningFile(false);
    if (error || !url) return;
    window.open(url, "_blank", "noopener");
  };

  const handleVoid = async () => {
    const reason = window.prompt("Reason for voiding this invoice (optional):") ?? undefined;
    setVoiding(true);
    const { ok } = await InstallerPayoutsService.voidInvoice(invoice.id, reason);
    setVoiding(false);
    if (!ok) return;
    onOpenChange(false);
    onVoided();
  };

  const handleParse = async () => {
    setParsing(true);
    setParseError(null);
    try {
      const { url, error: urlError } = await InstallerPayoutsService.getInvoiceDownloadUrl(invoice.file_path);
      if (urlError || !url) throw new Error("Could not open the invoice file");
      const fileRes = await fetch(url);
      if (!fileRes.ok) throw new Error("Could not download the invoice file");
      const blob = await fileRes.blob();
      const fileBase64 = await blobToBase64(blob);

      const { data, error: parseErr } = await supabase.functions.invoke("receipt-parse", {
        body: {
          fileName: `${invoice.invoice_number}`,
          mimeType: blob.type || "application/octet-stream",
          fileBase64,
        },
      });
      if (parseErr) throw parseErr;
      const receipt = (data as { receipt?: ParsedInvoice } | null)?.receipt;
      if (!receipt) throw new Error("No data extracted");
      setParsed(receipt);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : "AI parsing failed");
    } finally {
      setParsing(false);
    }
  };

  const breakdownLines = [
    { label: "Installation payout", amount: invoice.base_contract_amount },
    invoice.change_order_amount !== 0 ? { label: "Approved change orders", amount: invoice.change_order_amount } : null,
    invoice.field_material_amount !== 0 ? { label: "Field materials reimbursement", amount: invoice.field_material_amount } : null,
    invoice.incentive_amount !== 0 ? { label: "Approved incentives", amount: invoice.incentive_amount } : null,
  ].filter((line): line is { label: string; amount: number } => line !== null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invoice {invoice.invoice_number}</DialogTitle>
          <DialogDescription>
            {invoice.vendor_name} · submitted {new Date(invoice.submitted_at).toLocaleDateString()} ·{" "}
            {invoice.source_type === "auto_generated" ? "Auto-generated by Koredor" : "Uploaded by installer"}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/20 p-3 space-y-2 text-sm">
          <div className="flex items-center justify-between font-semibold">
            <span>Total</span>
            <span>{money(invoice.total)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground">Koredor-computed — system of record.</p>
          {breakdownLines.length > 1 && (
            <div className="border-t pt-2 space-y-1 text-xs">
              {breakdownLines.map((line) => (
                <div key={line.label} className="flex items-center justify-between text-muted-foreground">
                  <span>{line.label}</span>
                  <span className="tabular-nums">{money(line.amount)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <Badge variant="outline" className="text-[10px]">
              Payout status: {payout.status.replace(/_/g, " ")}
            </Badge>
            {invoice.missing_receipt_count > 0 && (
              <Badge variant="destructive" className="text-[10px]">
                {invoice.missing_receipt_count} field material{invoice.missing_receipt_count > 1 ? "s" : ""} missing a receipt
              </Badge>
            )}
          </div>
        </div>

        <Button variant="outline" size="sm" disabled={openingFile} onClick={handleViewFile} className="w-full">
          {openingFile ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="mr-1.5 h-3.5 w-3.5" />}
          View invoice file
        </Button>

        {invoice.source_type === "installer_upload" && (
          <div className="rounded-md border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">AI-extracted from file</span>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" disabled={parsing} onClick={handleParse}>
                {parsing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
                Parse with AI
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Informational only — for reference, never used to adjust the total above.
            </p>
            {parseError && <p className="text-xs text-destructive">{parseError}</p>}
            {parsed && (
              <div className="text-xs space-y-1 rounded bg-muted/30 p-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Vendor</span><span>{parsed.vendorName || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Invoice #</span><span>{parsed.invoiceNumber || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span>{parsed.purchasedAt || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total on file</span><span>{parsed.total ? money(parsed.total) : "—"}</span></div>
                {parsed.warnings?.length > 0 && (
                  <p className="text-muted-foreground italic pt-1">{parsed.warnings.join(" ")}</p>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            disabled={voiding || invoice.status === "void"}
            onClick={handleVoid}
          >
            {voiding ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Void & allow resubmit
          </Button>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
